import { ChevronLeft, ChevronRight } from "lucide-react";

const DAY_SHORT = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function fmtRange(monday) {
  const sunday = addDays(monday, 6);
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const fmt = (d, opts) => d.toLocaleString("en-US", opts);
  if (sameMonth) {
    return `${fmt(monday, { month: "short", day: "numeric" })} – ${fmt(sunday, { day: "numeric" })}`;
  }
  return `${fmt(monday, { month: "short", day: "numeric" })} – ${fmt(sunday, { month: "short", day: "numeric" })}`;
}

export default function MealDaySelector({
  weekStart,
  onPrevWeek,
  onNextWeek,
  weekDays,
  selectedDayKey,
  onSelectDay,
}) {
  return (
    <div className="space-y-3">
      {/* Week Navigation Header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-300">
          <span className="font-semibold text-white tracking-wide">
            {fmtRange(weekStart)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevWeek}
            aria-label="Previous week"
            className="w-7 h-7 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white transition active:scale-95 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onNextWeek}
            aria-label="Next week"
            className="w-7 h-7 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white transition active:scale-95 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Horizontal Mon - Sun Circular Selectors */}
      <div
        className="p-2 rounded-2xl bg-[#071326]/80 border border-white/10 shadow-sm"
        data-testid="meal-day-selector"
      >
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map(({ dayKey, dayIdx, date, hasPlan, hasLogs, isToday }) => {
            const isSelected = selectedDayKey === dayKey;

            return (
              <button
                key={dayKey}
                type="button"
                onClick={() => onSelectDay(dayKey, date)}
                data-testid={`meal-day-btn-${dayKey}`}
                className={`relative flex flex-col items-center py-2 px-1 rounded-xl transition-all select-none cursor-pointer ${
                  isSelected
                    ? "bg-gradient-to-b from-[#2C7BFF] to-[#0A47D8] text-white shadow-[0_0_16px_rgba(44,123,255,0.65),inset_0_1px_0_rgba(255,255,255,0.25)] border border-sky-400/40"
                    : "hover:bg-white/5 text-slate-400 hover:text-white"
                }`}
              >
                {/* Day name abbreviation */}
                <span
                  className={`text-[10px] font-bold tracking-wider mb-1 ${
                    isSelected
                      ? "text-white"
                      : isToday
                        ? "text-sky-400 font-extrabold"
                        : "text-slate-400"
                  }`}
                >
                  {DAY_SHORT[dayIdx]}
                </span>

                {/* Day number circle */}
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    isSelected
                      ? "bg-white text-slate-950 font-black shadow-sm"
                      : isToday
                        ? "border border-sky-400 text-sky-400 font-extrabold"
                        : "text-slate-200"
                  }`}
                >
                  {date.getDate()}
                </div>

                {/* Assigned plan or meal log indicator dot */}
                <div className="h-1.5 flex items-center justify-center mt-1">
                  {(hasPlan || hasLogs) && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        isSelected
                          ? "bg-white"
                          : hasLogs
                            ? "bg-emerald-400"
                            : "bg-sky-400"
                      }`}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
